import { ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils/src/cn";
import { FearGreedGauge } from "./FearGreedGauge";
import { AltcoinSeason } from "./AltcoinSeason";
import { BitcoinDominance } from "./BitcoinDominance";
import { AltcoinSeasonH5 } from "./AltcoinSeason/AltcoinSeasonH5";
import { FearGreedGaugeH5 } from "./FearGreedGauge/FearGreedGaugeH5";
import { BitcoinDominanceH5 } from "./BitcoinDominance/BitcoinDominanceH5";
import Translations from "@translate/components/Home/MarketIndicators/MarketIndicators";
import { I18nUtil } from "@utils/i18n";
import { formatBigNumber } from "@/src/utils";
import { HORIZONTAL_SCROLL_ATTR } from "@/src/hooks/useSwipeTabs";

const I18n = I18nUtil.createScoped(Translations);

/** 单个指数的行情数据结构，供指标与指数组件复用 */
export interface IndexTradingItem {
  change_price?: number;
  change_percentage?: number;
  current_price?: number;
}

/** 指数行情映射表，key 为 symbol，例如 ^GSPC、^NDX 等 */
export type IndexTradingData = Record<string, IndexTradingItem>;

export interface MarketIndicatorsProps {
  mode?: "row" | "column";
  /** 数据类型：crypto / stock，用于控制展示哪些指标 */
  type: "crypto" | "stock";
  data?: {
    market_metrics_latest?: {
      altcoin_index?: number;
      fear_and_greed_index?: number;
      btc_dominance?: string;
      eth_dominance?: string;
      btc_dominance_24h_percentage_change?: string;
      eth_dominance_24h_percentage_change?: string;
      /** 指数行情数据，供首页指数卡片等组件使用 */
      index_trading_data?: IndexTradingData;
    };
  };
  className?: string;
  fearGreedTitleClassName?: string;
  fearGreedStatusClassName?: string;
  fearGreedWidth?: number | string;
}

export const MarketIndicators = (props: MarketIndicatorsProps) => {
  const {
    type,
    data: rawData,
    className,
    mode = "column",
    fearGreedWidth,
    fearGreedTitleClassName,
    fearGreedStatusClassName,
  } = props || {};
  // 保持原组件内部命名为 data，尽量减少改动
  const { market_metrics_latest: data = {} } = rawData || {};

  const dominanceData = [
    {
      name: I18n.t("Bitcoin"),
      symbol: "BTC",
      percentage: Number(formatBigNumber(data.btc_dominance)),
      change: Number(formatBigNumber(data.btc_dominance_24h_percentage_change)),
      color: ospThemes.colors.content_aura_lv1_primary,
    },
    {
      name: I18n.t("Ethereum"),
      symbol: "ETH",
      percentage: Number(formatBigNumber(data.eth_dominance)),
      change: Number(formatBigNumber(data.eth_dominance_24h_percentage_change)),
      color: ospThemes.colors.content_brand_primary,
    },
  ];

  if (mode === "row") {
    return (
      <div
        {...{ [HORIZONTAL_SCROLL_ATTR]: "true" }}
        className={cn(
          "md:bg-background_white_overlay5 bg-transparent overflow-x-auto no-scrollbar",
          className,
        )}
      >
        <div className="flex flex-row gap-3 min-w-fit items-stretch">
          {/* Altcoin Season */}
          <div className="w-[160px] flex-shrink-0 flex">
            <AltcoinSeasonH5
              value={Number(data?.altcoin_index || 0)}
              className="px-3 py-2 rounded-3 border border-border_white_transparent10 w-full h-full"
              titleClassName={cn("body_medium_s text-content_tertiary")}
            />
          </div>
          {/* Fear & Greed */}
          <div className="w-[160px] flex-shrink-0 flex">
            <FearGreedGaugeH5
              value={Number(data?.fear_and_greed_index || 0)}
              className="px-3 py-2 rounded-3 border border-border_white_transparent10 w-full h-full"
              titleClassName={cn("body_medium_s text-content_tertiary")}
              width={fearGreedWidth}
            />
          </div>
          {/* Bitcoin Dominance */}
          <div className="w-[160px] flex-shrink-0 flex">
            <BitcoinDominanceH5
              data={dominanceData}
              className="px-3 py-2 rounded-3 border border-border_white_transparent10 w-full h-full"
              titleClassName={cn("body_medium_s text-content_tertiary")}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "pt-4 pb-6 rounded-3 md:bg-background_solid_neutral_primary bg-transparent",
        className,
      )}
    >
      {/* Fear & Greed：crypto / stock 都展示 */}
      <FearGreedGauge
        value={Number(data?.fear_and_greed_index || 0)}
        className="md:px-4 px-3 md:pb-6 pb-3"
        titleClassName={fearGreedTitleClassName}
        statusClassName={fearGreedStatusClassName}
        width={fearGreedWidth}
      />

      {/* 仅 crypto 展示 Altcoin Season 与 Dominance 等指标 */}
      {type === "crypto" && (
        <>
          <AltcoinSeason
            value={Number(data?.altcoin_index || 0)}
            className="px-4 py-6 border-t border-border_white_transparent10"
          />
          <BitcoinDominance
            data={dominanceData}
            className="px-4 pt-6 border-t border-border_white_transparent10"
          />
        </>
      )}
    </div>
  );
};
